/**
 * Layout the console's pages and their placeholders both consume (spec 079 FR-016).
 *
 * Here rather than in either component, and this is the whole mechanism behind the
 * fidelity rule: a skeleton that restates its content's grid is a second copy of that
 * layout, and the first person to add a column updates one of them. Nothing fails when
 * they drift — the placeholder simply stops matching, silently, which is the failure
 * this feature exists to remove.
 *
 * Sharing the *container* keeps the box identical while leaving what sits inside it
 * free to differ, which is exactly the line FR-011 and FR-015 draw between matching the
 * shape (required) and drawing the content (forbidden).
 */

/** The frame every console page sits in. */
export const ADMIN_PAGE_SECTION =
  'flex flex-1 flex-col gap-6 max-md:px-4 max-md:pt-4 p-6 pb-12';

/** The overview's summary tiles: four, in one row on a wide screen (spec 168). The
 *  period's spend moved up into the header's answer, which left the fifth tile alone
 *  on a second row no longer. */
export const ADMIN_TILE_GRID = 'grid gap-4 sm:grid-cols-2 lg:grid-cols-4';

/**
 * How many columns the workspace limits table has.
 *
 * A number rather than a copy of the headings: the placeholder needs the column *count*
 * to reserve the right width, and nothing else about them.
 *
 * **This constant has already drifted once, silently.** Spec 076 added a sixth column
 * ("Used", with its limit bar) while this feature was in review. The two branches touched
 * different parts of the same file, so git merged them without a conflict and left the
 * placeholder a column short — nothing failed, and nothing would have. A reader would have
 * seen a table skeleton that was subtly the wrong width and had no way to know why.
 *
 * The sharing rule this file exists for (spec 079 FR-016) covers *containers*: a grid is
 * exported once and imported by both sides, so it cannot drift. A count is not a container,
 * and that is the hole. It is closed by a guard rather than by a refactor —
 * `tests/shared/pendingSurface.test.ts` counts the `<th scope="col">` elements in the page
 * and fails if this number disagrees with them. Change the table, and the build tells you
 * to change this line.
 */
export const LIMITS_TABLE_COLUMN_COUNT = 6;

/**
 * How many columns Admin → Workspaces' table has (spec 195): workspace,
 * visibility, owners, members, last activity. Guarded against the table the same
 * way as the limits table's count above.
 */
export const WORKSPACES_TABLE_COLUMN_COUNT = 5;

/**
 * How many columns the LLM profiles admin grid has (spec 136 FR-002, FR-003).
 *
 * The same hole as its neighbour above, and closed the same way: a count is not a
 * container, so `tests/shared/pendingSurface.test.ts` counts the `<th scope="col">` and
 * `<SortHeader>` elements in `LlmProfilesTable`'s header row and fails if this number
 * disagrees with them. Add an eleventh column, and the build tells you to change this line.
 *
 * **Load-bearing twice**, which is why it is worth the comment. It sets the placeholder's
 * reserved width *and* the `colSpan` of the expanded row's single detail cell. The guard
 * pins this number to the table's own header row, which is the invariant both consumers
 * rest on, so both stay correct as long as both import this constant rather than writing
 * `10`.
 *
 * Both readers reach it through `@/features/organization-admin`'s barrel rather than this
 * module path (R-F6, decision A): this is a `llm-profiles` rendering detail living in a
 * sibling because the shared guard reads one layout module, and the barrel is the seam
 * that keeps that borrowing to the package's public surface. If a third such grid
 * appears, promote the convention into a shared module (e.g. `src/shared/pendingSurface`)
 * instead of growing this file with per-feature entries (R-F61).
 */
export const LLM_PROFILES_TABLE_COLUMN_COUNT = 10;

/**
 * Rows the LLM profiles grid's placeholder reserves (research D7, as above).
 *
 * Its own constant rather than a reuse of `PLACEHOLDER_ROW_COUNT`: the two surfaces happen
 * to agree today, and tying them together would mean tuning one grid's skeleton silently
 * retunes the other's.
 */
export const LLM_PROFILES_PLACEHOLDER_ROW_COUNT = 4;

/** The Limits page's three policy cards (spec 117), shared with their placeholder. */
export const LIMITS_POLICY_GRID = 'grid shrink-0 gap-3 sm:grid-cols-3';

/** Rows a table placeholder reserves before the real count is known (research D7). */
export const PLACEHOLDER_ROW_COUNT = 4;
